import { useState } from "react";
import { escapeHtml, formatRelativeTime } from "../../../utils";
import { highlightMentions } from "../../../mentions";
import { MentionInput } from "./MentionInput";
import type { AlbumComment } from "../../../types";

function initials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function CommentItem({
  comment,
  isReply,
  isOwn,
  liked,
  isEditing,
  isReplying,
  onLike,
  onToggleReply,
  onToggleEdit,
  onDelete,
  onSubmitReply,
  onSubmitEdit,
}: {
  comment: AlbumComment;
  isReply: boolean;
  isOwn: boolean;
  liked: boolean;
  isEditing: boolean;
  isReplying: boolean;
  onLike: () => void;
  onToggleReply: () => void;
  onToggleEdit: () => void;
  onDelete: () => void;
  onSubmitReply: (text: string) => void;
  onSubmitEdit: (text: string) => void;
}) {
  const [replyValue, setReplyValue] = useState("");
  const [editValue, setEditValue] = useState(comment.text);
  const likes = Array.isArray(comment.likes) ? comment.likes : [];

  return (
    <div
      className={`album-comment d-flex gap-2 ${isReply ? "album-comment-reply" : ""}`}
      data-comment-id={comment.id}
      role="listitem"
    >
      <span className="album-comment-avatar flex-shrink-0" aria-hidden="true">
        {initials(comment.authorName)}
      </span>
      <div className="min-w-0 flex-grow-1">
        {isEditing ? (
          <form
            className="album-comment-edit-form mt-1"
            onSubmit={(e) => {
              e.preventDefault();
              const text = editValue.trim();
              if (text) onSubmitEdit(text);
            }}
          >
            <MentionInput
              value={editValue}
              onChange={setEditValue}
              multiline
              rows={2}
              maxLength={1000}
              className="form-control form-control-sm album-comment-edit-input"
              autoFocus
            />
            <div className="d-flex gap-2 mt-1 justify-content-end">
              <button
                type="button"
                className="album-comment-action-btn"
                aria-label="Cancel edit"
                title="Cancel"
                onClick={onToggleEdit}
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
              <button type="submit" className="album-comment-action-btn" aria-label="Save edit" title="Save">
                <i className="bi bi-check-lg" aria-hidden="true" />
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="small">
              <span className="fw-semibold">{comment.authorName}</span>{" "}
              <span
                className="album-comment-text"
                dangerouslySetInnerHTML={{ __html: highlightMentions(escapeHtml(comment.text)) }}
              />
            </div>
            <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
              <span className="album-comment-time muted">{formatRelativeTime(comment.createdAt)}</span>
              {comment.updatedAt && <span className="album-comment-edited muted">edited</span>}
              {likes.length > 0 && (
                <span className="album-comment-like-count muted">
                  {likes.length} like{likes.length === 1 ? "" : "s"}
                </span>
              )}
              <button
                type="button"
                className={`album-comment-action-btn comment-like-btn ${liked ? "is-active" : ""}`}
                aria-label={liked ? "Unlike" : "Like"}
                title={liked ? "Unlike" : "Like"}
                aria-pressed={liked}
                onClick={onLike}
              >
                <i className={`bi ${liked ? "bi-heart-fill" : "bi-heart"}`} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="album-comment-action-btn"
                aria-label="Reply"
                title="Reply"
                onClick={onToggleReply}
              >
                <i className="bi bi-reply" aria-hidden="true" />
              </button>
              {isOwn && (
                <button
                  type="button"
                  className="album-comment-action-btn"
                  aria-label="Edit"
                  title="Edit"
                  onClick={() => {
                    setEditValue(comment.text);
                    onToggleEdit();
                  }}
                >
                  <i className="bi bi-pencil" aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                className="album-comment-action-btn"
                aria-label="Delete"
                title="Delete"
                onClick={onDelete}
              >
                <i className="bi bi-trash" aria-hidden="true" />
              </button>
            </div>
            {isReplying && (
              <form
                className="album-comment-reply-form d-flex gap-2 mt-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const text = replyValue.trim();
                  if (text) {
                    onSubmitReply(text);
                    setReplyValue("");
                  }
                }}
              >
                <MentionInput
                  value={replyValue}
                  onChange={setReplyValue}
                  placeholder={`Reply to ${comment.authorName}...`}
                  maxLength={1000}
                  className="form-control form-control-sm album-comment-reply-input"
                  autoFocus
                />
                <button type="submit" className="album-comment-action-btn" aria-label="Send reply" title="Send reply">
                  <i className="bi bi-send-fill" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="album-comment-action-btn"
                  aria-label="Cancel reply"
                  title="Cancel"
                  onClick={onToggleReply}
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
